import { useState, ReactNode, useCallback, useMemo, MouseEvent } from 'react';
import { startOfDay, startOfMonth, addDays, endOfMonth, differenceInDays, format, addMonths, isSameMonth } from 'date-fns';

import './Calendar.scss';

export interface CalendarProps {
    value: Date | undefined;
    min?: Date;
    max?: Date;
    onChange?: (value: Date) => void;
    onFocusCapture?: () => void;
}

let DAYS_OF_WEEK: ReactNode[] | undefined;

function getCalendar(date: Date) {
    const today = startOfDay(date);
    const firstOfMonth = startOfMonth(today);
    const firstSunday = addDays(firstOfMonth, -firstOfMonth.getDay());
    const lastOfMonth = endOfMonth(today);
    const lastSaturday = addDays(lastOfMonth, 7 - lastOfMonth.getDay());
    const numDays = differenceInDays(lastSaturday, firstSunday);
    const numWeeks = Math.ceil(numDays / 7);
    return { today, firstOfMonth, firstSunday, lastOfMonth, numDays, numWeeks, lastSaturday };
}

const TODAY = startOfDay(new Date());

export function Calendar(props: CalendarProps) {
    const [reference, setReference] = useState(props.value ?? TODAY);

    const { firstSunday, numWeeks, lastSaturday } = useMemo(() => getCalendar(reference), [reference]);

    if (!DAYS_OF_WEEK) {
        DAYS_OF_WEEK = Array.from({ length: 7 }, (_, i) => <th key={i}>{format(addDays(firstSunday, i), 'EEEEE')}</th>);
    }

    const { onChange } = props;
    const handleChangeClick = useCallback(
        function (e: MouseEvent<HTMLButtonElement>) {
            if (onChange) {
                const date = new Date(parseInt(e.currentTarget.dataset.value || '', 10));
                onChange(date);
            }
        },
        [onChange]
    );

    const { min, max } = props;
    const renderDay = useCallback(
        function (week: number, day: number) {
            const renderDate = addDays(firstSunday, week * 7 + day);
            return (
                <td key={day}>
                    <button
                        onClick={handleChangeClick}
                        type="button"
                        data-value={renderDate.getTime()}
                        disabled={(min && renderDate < min) || (max && renderDate > max)}
                        className={`${isSameMonth(renderDate, reference) ? '' : 'text-muted'}`}
                    >
                        {renderDate.getDate()}
                    </button>
                </td>
            );
        },
        [firstSunday, reference, min, max, handleChangeClick]
    );

    const renderWeek = useCallback(
        (_: number, week: number) => <tr key={week}>{Array.from({ length: 7 }, (_, day) => renderDay(week, day))}</tr>,
        [renderDay]
    );

    const handleMonthClick = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            const months = parseInt(e.currentTarget.dataset.months || '', 10);
            setReference((x) => addMonths(x, months));
        },
        [setReference]
    );

    return (
        <table className="calendar" onFocusCapture={props.onFocusCapture}>
            <thead>
                <tr>
                    <td>
                        <button
                            title="Previous"
                            disabled={min && min >= firstSunday}
                            type="button"
                            data-months="-1"
                            onClick={handleMonthClick}
                        >
                            &laquo;
                        </button>
                    </td>
                    <td colSpan={5} className="calendar-month" title="Current">
                        {format(reference, 'MMMM yyyy')}
                    </td>
                    <td>
                        <button title="Next" disabled={max && max <= lastSaturday} type="button" data-months="1" onClick={handleMonthClick}>
                            &raquo;
                        </button>
                    </td>
                </tr>
                <tr>{DAYS_OF_WEEK}</tr>
            </thead>
            <tbody>{Array.from({ length: numWeeks }, renderWeek)}</tbody>
        </table>
    );
}
